/**
 * SoleadLab 2.0 — Claim Store
 *
 * B3.4: Zustand store for claim entity CRUD with status workflow.
 *
 * Claims are mutable — updates use optimistic concurrency (If-Match).
 * V4 Invariant #5: creating a claim requires the referenced finding
 * to already be 'approved'. The store surfaces this as a specific
 * error message so the UI can guide through the approved-finding flow.
 *
 * Site-scoped: call setSiteId to switch sites, which clears stale items.
 *
 * State:
 *   items     — cached claims for the current site
 *   loading   — fetch in progress
 *   error     — last error message (null if none)
 *   siteId    — current site scope
 *
 * Actions:
 *   setSiteId(id)               — switch site scope (clears + refetches)
 *   fetchItems(filters?)        — load claims for current site
 *   getById(id)                 — lookup a cached item by ID
 *   getByFindingId(findingId)   — lookup all claims for an approved finding
 *   create(claim)               — create a new claim (finding must be approved)
 *   update(id, updates, ver)    — update with optimistic concurrency
 *   transition(id, status, ver) — convenience: update status only
 *   clear()                     — reset store state
 */

import { create } from 'zustand';
import {
  fetchClaims,
  createClaim,
  updateClaim,
  isConflictError,
  isStatusTransitionError,
  isFindingNotApprovedError,
  type Claim,
  type ClaimStatus,
  type ClaimFilters,
} from '../services/claimApi';

interface ClaimState {
  items: Claim[];
  loading: boolean;
  error: string | null;
  siteId: string | null;

  setSiteId: (siteId: string) => void;
  fetchItems: (filters?: ClaimFilters) => Promise<void>;
  getById: (id: string) => Claim | undefined;
  getByFindingId: (findingId: string) => Claim[];
  create: (
    claim: Parameters<typeof createClaim>[1],
  ) => Promise<Claim>;
  update: (
    id: string,
    updates: Parameters<typeof updateClaim>[2],
    version: number,
  ) => Promise<Claim>;
  transition: (id: string, status: ClaimStatus, version: number) => Promise<Claim>;
  clear: () => void;
}

export const useClaimStore = create<ClaimState>((set, get) => ({
  items: [],
  loading: false,
  error: null,
  siteId: null,

  setSiteId: (siteId: string) => {
    const current = get().siteId;
    if (current === siteId) return;
    set({ siteId, items: [], error: null });
  },

  fetchItems: async (filters?: ClaimFilters) => {
    const { siteId } = get();
    if (!siteId) return;

    set({ loading: true, error: null });
    try {
      const result = await fetchClaims(siteId, filters);
      set({ items: result.items, loading: false });
    } catch (err) {
      set({
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to fetch claims',
      });
    }
  },

  getById: (id: string) => {
    return get().items.find((item) => item.id === id);
  },

  getByFindingId: (findingId: string) => {
    return get().items.filter((item) => item.findingId === findingId);
  },

  create: async (claim) => {
    const { siteId } = get();
    if (!siteId) throw new Error('No site selected');

    try {
      const created = await createClaim(siteId, claim);
      set({ items: [...get().items, created] });
      return created;
    } catch (err) {
      if (isFindingNotApprovedError(err)) {
        // Surface the V4 invariant violation clearly: guide the user to approve the finding first
        set({
          error: 'Cannot create claim: the referenced finding must be approved first. ' +
                 'Complete the approved-finding workflow before creating a claim.',
        });
      }
      throw err;
    }
  },

  update: async (id, updates, version) => {
    const { siteId } = get();
    if (!siteId) throw new Error('No site selected');

    try {
      const updated = await updateClaim(siteId, id, updates, version);
      set({
        items: get().items.map((item) =>
          item.id === id ? updated : item,
        ),
      });
      return updated;
    } catch (err) {
      if (isConflictError(err)) {
        set({ error: 'Version conflict — another update occurred. Please refresh.' });
      } else if (isStatusTransitionError(err)) {
        set({ error: err instanceof Error ? err.message : 'Invalid status transition.' });
      }
      throw err;
    }
  },

  transition: async (id, status, version) => {
    return get().update(id, { status }, version);
  },

  clear: () => {
    set({ items: [], siteId: null, error: null, loading: false });
  },
}));
